/* eslint-disable */
// @ts-nocheck
/* ============================================================================
 * MTE — Placeholders héros & Sábio (D-NX-066 P3, 2026-09-09)
 * ----------------------------------------------------------------------------
 * Source-of-truth : shared/mte-design/04-heroes-and-mentor-brief.md §2.7
 * Owner : Palette. Référence paste-ready pour Atelier.
 *
 * Stratégie double :
 *  - Option A : emoji (livraison immédiate, lisibilité cross-OS ✅)
 *  - Option B : SVG abstrait minimal (cercle coloré + initiale du rôle)
 *
 * Les deux options exposent la même grille (6 héros + Sábio) et la même API
 * `<HeroPlaceholderGrid />`. L'Atelier peut basculer via prop `mode="emoji" | "svg"`.
 *
 * ⚠️  Référence clairement marquée — à remplacer par les illustrations finales
 *     Ana dès livraison vague 2 (D-NX-066 « placeholders clairement marqués »).
 * ========================================================================== */

import React from 'react';

export type HeroId = 'ninja' | 'lobo' | 'princesa' | 'fada' | 'gato' | 'dragao' | 'sage';

export interface HeroCell {
  id: HeroId;
  label: string;          /* nom interne (libellé éditeur, JAMAIS visible enfant) */
  role: string;           /* ex. "L'artiste curieuse" — rôle affiché à l'enfant */
  emoji: string;
  accent: string;         /* var(--accent-*) token name */
  bgColor: string;        /* hex fallback pour Option B */
}

export const HERO_CELLS: ReadonlyArray<HeroCell> = [
  { id: 'ninja',    label: 'Ninja',    role: 'Le stratège silencieux',  emoji: '🥷', accent: '--accent-ninja',    bgColor: '#5B3FB0' },
  { id: 'lobo',     label: 'Lobo',     role: "L'éclaireur courageux",  emoji: '🐺', accent: '--accent-lobo',     bgColor: '#4F8A4A' },
  { id: 'princesa', label: 'Princesa', role: 'La gardienne du cœur',   emoji: '👸', accent: '--accent-princesa', bgColor: '#D67A8C' },
  { id: 'fada',     label: 'Fada',     role: "L'éveilleuse d'idées",    emoji: '🧚', accent: '--accent-fada',     bgColor: '#4FB8C9' },
  { id: 'gato',     label: 'Gato',     role: 'Le curieux discret',     emoji: '🐱', accent: '--accent-gato',     bgColor: '#D8953A' },
  { id: 'dragao',   label: 'Dragão',   role: 'Le protecteur vaillant', emoji: '🐉', accent: '--accent-dragao',   bgColor: '#C84B2C' },
  { id: 'sage',     label: 'Sábio',    role: 'Le guide sage',          emoji: '🦉', accent: '--world-crystal-500', bgColor: '#4A8FCB' },
] as const;

/* === Mode A — emoji ===================================================== */

export function HeroCellEmoji({ cell }: { cell: HeroCell }) {
  return (
    <div
      data-placeholder="emoji"
      data-hero-id={cell.id}
      style={{
        display: 'grid',
        placeItems: 'center',
        width: '100%',
        aspectRatio: '1 / 1',
        background: `var(${cell.accent})`,
        borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-magical)',
      }}
      aria-hidden="true"
    >
      <span style={{ fontSize: 'clamp(2.5rem, 8vw, 4.5rem)', lineHeight: 1 }}>
        {cell.emoji}
      </span>
    </div>
  );
}

/* === Mode B — SVG abstrait minimal ====================================== */

export function HeroCellSvg({ cell }: { cell: HeroCell }) {
  const initial = cell.id === 'sage' ? 'S' : cell.label.charAt(0);
  return (
    <svg
      data-placeholder="svg"
      data-hero-id={cell.id}
      viewBox="0 0 100 100"
      role="img"
      aria-label={`${cell.label} (placeholder)`}
      style={{
        width: '100%',
        height: 'auto',
        aspectRatio: '1 / 1',
        borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-magical)',
      }}
    >
      <defs>
        <linearGradient id={`g-${cell.id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={cell.bgColor} stopOpacity="1" />
          <stop offset="100%" stopColor={cell.bgColor} stopOpacity="0.65" />
        </linearGradient>
      </defs>
      <rect width="100" height="100" rx="20" fill={`url(#g-${cell.id})`} />
      <text
        x="50"
        y="58"
        textAnchor="middle"
        fontSize="48"
        fontWeight="700"
        fill="#FFFFFF"
        fontFamily="var(--font-display)"
      >
        {initial}
      </text>
      <text
        x="50"
        y="82"
        textAnchor="middle"
        fontSize="10"
        fill="#FFFFFF"
        opacity="0.7"
        fontFamily="var(--font-ui)"
      >
        placeholder
      </text>
    </svg>
  );
}

/* === Grille complète (mode A ou B) ===================================== */

export interface HeroPlaceholderGridProps {
  mode?: 'emoji' | 'svg';
  context?: 'enfant' | 'adulte';
  showSage?: boolean; /* true par défaut — Sábio fait partie de l'écran d'accueil */
}

export function HeroPlaceholderGrid({
  mode = 'emoji',
  context = 'enfant',
  showSage = true,
}: HeroPlaceholderGridProps) {
  const cells = showSage ? HERO_CELLS : HERO_CELLS.slice(0, 6);
  return (
    <div
      data-placeholder-grid={mode}
      data-context={context}
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: 'var(--space-4)',
        padding: 'var(--space-5)',
        maxWidth: 'var(--container-child)',
        margin: '0 auto',
      }}
      aria-label="Placeholders héros — à remplacer par les illustrations finales"
    >
      {cells.map((cell) => (
        <figure
          key={cell.id}
          style={{
            margin: 0,
            display: 'grid',
            gap: 'var(--space-2)',
          }}
        >
          {mode === 'emoji'
            ? <HeroCellEmoji cell={cell} />
            : <HeroCellSvg cell={cell} />}
          <figcaption
            style={{
              font: 'var(--weight-medium) var(--text-sm) var(--font-display)',
              textAlign: 'center',
              color: 'var(--text-primary)',
            }}
          >
            {cell.role}
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

/* === Indicateur « placeholder » (badge pour atelier) ================== */

export function PlaceholderBadge() {
  return (
    <span
      role="note"
      aria-label="Placeholder — illustration temporaire en attente des assets finaux"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-1)',
        padding: 'var(--space-1) var(--space-2)',
        background: 'var(--world-light-300)',
        color: 'var(--world-wood-900)',
        borderRadius: 'var(--radius-pill)',
        font: 'var(--weight-semibold) var(--text-xs) var(--font-ui)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      🟡 Placeholder — vague 2
    </span>
  );
}

/* === Mapping data pour Atelier data model ============================== */

export interface HeroCatalogEntry {
  id: HeroId;
  emoji: string;
  accentToken: `--accent-${string}` | '--world-crystal-500';
  isMentor: boolean;
}

export const HERO_CATALOG: ReadonlyArray<HeroCatalogEntry> = HERO_CELLS.map(
  (cell) => ({
    id: cell.id,
    emoji: cell.emoji,
    accentToken: cell.accent as `--accent-${string}` | '--world-crystal-500',
    isMentor: cell.id === 'sage',
  })
);

/* ============================================================================
 * Notes intégration Atelier :
 *  - HERO_CELLS est la source-of-truth (label / role / emoji / accent).
 *  - HERO_CATALOG est prêt à être importé comme type dans le data model.
 *  - Mode "emoji" = livraison immédiate, mobile-cross-OS ✅
 *  - Mode "svg"   = design system propre sans dépendance emoji-fonte.
 *  - Remplacement vague 2 : remplacer `<HeroCellEmoji/Svg />` par `<HeroIllustration />`
 *    pointant vers `/assets/heroes/ninja.svg` etc. Aucun autre changement.
 * ========================================================================== */
